import { History } from "history";
import { ResourceStrings } from "./ResourceStrings";
import { AppPaths } from "./constants/urls";
import { hasItems } from "./utils/collections";
import { App } from './app';
import { PrincipalId } from "./RootPost";
import { AccountType } from "./communication/PlatformActions";

// @TODO: Add some text saying you are logged on using your Visual Studio account
// Show user logo

interface OnBeforeSignInEventHandler {
    preventDefault: () => void;
}

const beforeSignIn = (e: OnBeforeSignInEventHandler): void => {
    e.preventDefault();
    App.requestSignOutAndIn();
}

/** Needed because MeControl does not refresh correctly if double-initialized; Used since we double-init MeControl after we load user ID */
let vsidOrIdStatic: string | PrincipalId = "";

export function generateSwitchUrl(info?: any): string {
    const returnUrl = window.location.href;
    const loginHint: string | null = info?.nextAccount?.login_hint ?? info?.nextAccount?.memberName; // from MeControl's IAccountBase interface. login hint or  email of user that can be used as login hint.
    const loginHintUrlPartIfAny: string = loginHint ? `&login_hint=${encodeURIComponent(loginHint)}` : "";
    return `/switch?ru=${encodeURIComponent(returnUrl)}${loginHintUrlPartIfAny}`;
}

const loadShell = (name: string = "", email: string = "", vsidOrPrincipalId: string | PrincipalId = "", isModerator: boolean = false, accountType?: AccountType): void => {
    vsidOrIdStatic = vsidOrPrincipalId ?? "";
    const meCtrlApi = (window as any).MeControl?.API;
    if (meCtrlApi?.addEventListener) {
        meCtrlApi?.removeEventListener('beforesignin', beforeSignIn);   // remove listener in case it was already registered. if none was registered then it will result in no-op
        meCtrlApi?.addEventListener('beforesignin', beforeSignIn);
    }

    // some of the valid origins:
    //  https://localhost:44342/signin-visualstudio
    //  https://developercommunity.visualstudio.com/signin-visualstudio
    //  https://developercommunity.azure.com/signin-visualstudio
    //  https://developercommunity.microsoft.com/signin-visualstudio
    //  ...
    // new origins have to be registered in first-party app portal for appid below
    // if origin is incorrect, we'll get no remembered accounts
    // Example: inspect returned HTML for these DevCom 1st party - working:
    // https://login.microsoftonline.com/savedusers?wreply=https%3A%2F%2Fdevelopercommunity.visualstudio.com%2Fsignin-visualstudio&appid=bbb1f62d-6019-4fdc-96f1-808091484dce&partnerId=visualstudiocommunity
    // https://login.microsoftonline.com/savedusers?wreply=https%3A%2F%2Flocalhost%3A44342%2Fsignin-visualstudio&appid=bbb1f62d-6019-4fdc-96f1-808091484dce&partnerId=visualstudiocommunity
    const REMEMBERED_ACCOUNTS_URL_AAD = `https://login.microsoftonline.com/savedusers?wreply=${encodeURIComponent(window.location.origin + "/signin-visualstudio")}&appid=bbb1f62d-6019-4fdc-96f1-808091484dce&partnerId=visualstudiocommunity`;

    const settings: any = {
        // see https://mecontroltestpartner.azurewebsites.net/PartnerDocs/articles/guides/upgradingToV2.html
        meControlOptions: {
            apiGeneration: "GEN2",
            currentAccount: null,
            authProviderConfig: {
                type: 'webAadWithMsaFed', // options: 'webAadWithMsaFed', 'webMsaOnly', 'webDualStack' and others
                appSignInUrl: generateSwitchUrl, // switch URL is used for regular sign-in as well
                appSignInToUrl: generateSwitchUrl,
                appSignOutUrl: '/signout',
                appSwitchUrl: generateSwitchUrl,
                appSwitchToUrl: generateSwitchUrl, // login_hint will be added by MeControl
                // msa: { // unused for type 'webAadWithMsaFed'. Commented out for illustration/debugging if needed.
                //     signOutAndForgetUrl: '/signout',
                //     rememberedAccountsUrl: `https://login.live.com/Me.srf?wa=wsignin1.0&wreply=${encodeURIComponent(window.location.origin)}&uaid=bbb1f62d-6019-4fdc-96f1-808091484dce&partnerId=visualstudiocommunity`, // MeControl will go to this URL and ask it to provide rememebered accounts to the browser
                //     // Examples:
                //     // Outlook:
                //     // https://login.live.com/Me.srf?wa=wsignin1.0&id=292841&wreply=https%3A%2F%2Foutlook.live.com%2Fmail%2F&mkt=en-US&uaid=cbe37ccb-589f-4d65-3e1f-14a805e76705&partnerId=exchange&idpflag=proxy
                //     // VS:
                //     // https://login.live.com/me.srf?wa=wsignin1.0&wreply=https%3A%2F%2Fvisualstudio.microsoft.com&uaid=e243ecbf-6dfe-4bbe-f460-8d118b38716d&partnerId=visualstudio
                // },
                aad: {
                    signOutUrl: 'https://login.microsoftonline.com/uxlogout', // TODO: implement
                    signOutAndForgetUrl: 'https://login.microsoftonline.com/uxlogout', // TODO: implement
                    forgetUrl: 'https://login.microsoftonline.com/forgetuser', // TODO: implement
                    rememberedAccountsUrl: REMEMBERED_ACCOUNTS_URL_AAD,
                }
            },
            appContextConfig: {
                commands: [],
            }
        }
    };

    if (name) {
        settings.meControlOptions.currentAccount = {
            type: accountType,
            displayName: name, // use displayName to override MeControl's saved accounts cache. firstName/lastName will be erased by MeControl's displayName from saved accounts cache.
            memberName: email,
            cid: '',
            authenticatedState: 'signedIn',
        };
        const commandList: any[] = [
            {
                type: "link",
                id: 'link_preferences',
                text: ResourceStrings.profileAndPreferences(),
                onClick: () => {
                    return PushRoute(App.GetUserProfileLink(vsidOrIdStatic), true);
                },
            },
        ];

        // All MSFT employees can see part of admin page, such as routing and read-only config
        if (isModerator || email?.endsWith("microsoft.com")) {
            commandList.push({
                type: "link",
                id: 'link_admin',
                text: 'Administration',
                onClick: () => {
                    return PushRoute(`/admin`, false);
                },
            });
        }

        const itemsLoaded: boolean = hasItems((window as any).msCommonShell?.meControlOptions?.()?.appContextConfig?.commands);
        settings.meControlOptions.appContextConfig.commands = itemsLoaded ? [] : commandList;
    } else {
        // User is not signed in so clear sign-in info if set earlier
        settings.meControlOptions.currentAccount = null;
    }

    (window as any).onShellReadyToLoad = null;
    (window as any).msCommonShell.load(settings);
};


// // const getProfile = () => {
// //     (window as any).onShellReadyToLoad = null;
// //     loadShell();
// // };

export const loadMeControl = (name: string, email: string, vsidOrPrincipalId: string | PrincipalId, isModerator: boolean = false, accountType: AccountType | null): void => {
    accountType = accountType === AccountType.aad ? AccountType.aad : AccountType.msa; // consider non-AAD accounts as MSA since that's what MeControlV2 supports
    // Callback to initialize UHF.
    if ((window as any).msCommonShell) {
        loadShell(name, email, vsidOrPrincipalId, isModerator, accountType);
    } else {
        (window as any).onShellReadyToLoad = () => loadShell(name, email, vsidOrPrincipalId, isModerator, accountType);
    }
}

export const signedOut = (): void => {
    if ((window as any).msCommonShell) {
        loadShell();
    } else {
        (window as any).onShellReadyToLoad = () => loadShell();
    }
}

// Navigate to a new route from outside of React root in a way which works with react router
const PushRoute = (path: string, preserveQueryParams: boolean, queryParams?: string[][]): boolean => {
    const routerHistory = (window as any).routerHistory as History;
    if (routerHistory?.push) {
        const params: URLSearchParams = new URLSearchParams(preserveQueryParams ? routerHistory.location.search : undefined);
        if (queryParams) {
            queryParams.forEach(item => {
                params.set(item[0], item[1]);
            });
        }

        const paramsString: string = params?.toString();
        if (paramsString) {
            routerHistory.push(`${path}?${paramsString}`);
        }
        else {
            routerHistory.push(path);
        }
    }
    return false;
}

